import { useEffect, useState } from 'react';

import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { DateTimeInput } from 'cpool-components/input/DateTimeInput';
import type { SUB_STATUSES_TYPE } from 'cpool-constants';
import {
    AGGREGATE_NAMES,
    LOANREQUEST_STATUSES,
    MAIL_FAILURE_REASONS,
    STATUS_MAIL_PREVIEW_KINDS,
    SUB_STATUS_IN_PROGRESS,
} from 'cpool-constants'; // eslint-disable-line @typescript-eslint/consistent-type-imports
import type { Schema_Status } from 'cpool-schemas/src/LoanRequest/status/status';
import type { SchemaUserResolver_GetAclPermissions } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuAlarmClock, LuCheck, LuCircleX, LuMail, LuSave, LuX } from 'react-icons/lu';

import { hasAnyMailTrigger } from './hasAnyMailTrigger';
import { useSubstatusMutation } from './useSubstatusMutation';
import { getDefaultResubmissionDate } from './utils/getDefaultResubmissionDate';
import { APIError } from '~/api/APICommunicator';
import { SendEmailChoice } from '~/components/SendEmailChoice';
import { useSendEmailChoice } from '~/components/SendEmailChoice/hooks/useSendEmailChoice';
import { AddNoteCard } from '~/components/StatusChangeNote/AddNoteCard';
import { useStatusChangeNote } from '~/components/StatusChangeNote/hooks/useStatusChangeNote';
import { StatusMailPreview } from '~/components/StatusMailPreview';
import { STATUS_MAIL_DIALOG_HEIGHT } from '~/components/StatusMailPreview/constants';
import { RESUBMISSION_MINUTE_STEP } from '~/constants';
import { useLoanRequestStatusesWithMailTrigger } from '~/features/leadDetails/hooks/useLoanRequestStatusesWithMailTrigger';
import { useStatusMailPreview } from '~/features/leadDetails/hooks/useStatusMailPreview';
import { dateFormat, timeFormat, weekdayFormat } from '~/lib/i18n';
import { buildAclPermissions } from '~/utils/buildAclPermissions';
import { cn } from '~/utils/cnTailwind';
import { getSubStatusTranslationPath } from '~/utils/getSubStatusTranslationPath';
import { loanRequestStatusesWithSubStatuses } from '~/utils/loanRequestStatusesWithSubStatuses';

const PERMISSIONS_FAIL_CLOSED = buildAclPermissions(false);

export type StatusWithSubStatuses = keyof typeof loanRequestStatusesWithSubStatuses;

export type SubstatusChangeFormProps = {
    currentStatus: Schema_Status['status'];
    currentSubStatus?: SUB_STATUSES_TYPE | null;
    leadId: string;
    onClose: () => void;
    open: boolean;
    permissions?: SchemaUserResolver_GetAclPermissions;
    resubmission: number | null | undefined;
    targetStatus?: StatusWithSubStatuses;
    withHeader?: boolean;
};

type SelectedStatusData = Exclude<Schema_Status, { status: typeof LOANREQUEST_STATUSES.CANCELED }>;

const isStatusWithSubStatuses = (status: Schema_Status['status']): status is StatusWithSubStatuses =>
    status in loanRequestStatusesWithSubStatuses;

const hasMailTriggerForSubstatus = (
    statusesWithMailTrigger: ReturnType<typeof useLoanRequestStatusesWithMailTrigger>['data'],
    status: StatusWithSubStatuses,
    subStatus: string,
): boolean => {
    if (!statusesWithMailTrigger) return false;
    const selectedStatus: Partial<Record<string, boolean>> = statusesWithMailTrigger[status];
    return selectedStatus[subStatus] ?? false;
};

export const SubstatusChangeForm: React.FC<SubstatusChangeFormProps> = ({
    currentStatus,
    currentSubStatus,
    leadId,
    onClose,
    open,
    permissions = PERMISSIONS_FAIL_CLOSED,
    resubmission,
    targetStatus: targetStatusProp,
    withHeader = false,
}) => {
    const { t } = useTranslation();
    const { data: statusesWithMailTrigger, isLoading: isMailTriggersLoading } = useLoanRequestStatusesWithMailTrigger();
    const mutation = useSubstatusMutation();
    const targetStatus = targetStatusProp ?? (isStatusWithSubStatuses(currentStatus) ? currentStatus : undefined);

    const [selectedSubStatus, setSelectedSubStatus] = useState<SUB_STATUSES_TYPE | undefined>();
    const emailChoice = useSendEmailChoice();
    const { reset: resetEmailChoice, sendEmail } = emailChoice;
    const [resubmissionDate, setResubmissionDate] = useState<Date | null>(null);

    useEffect(() => {
        if (open) return;

        setSelectedSubStatus(undefined);
        resetEmailChoice();
        setResubmissionDate(null);
    }, [open, resetEmailChoice]);

    useEffect(() => {
        if (!open || !targetStatus) return;

        setSelectedSubStatus(targetStatus === currentStatus ? currentSubStatus ?? undefined : undefined);
        resetEmailChoice();
    }, [currentStatus, currentSubStatus, open, resetEmailChoice, targetStatus]);

    const hasChangeStatusPermission = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].changeStatus;
    const hasCreateNotePermission = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].createNote;
    const hasSetResubmissionPermission = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].setResubmission;
    const isResubmissionSelected =
        targetStatus === LOANREQUEST_STATUSES.IN_PROGRESS && selectedSubStatus === SUB_STATUS_IN_PROGRESS.RESUBMISSION;
    const isResubmissionPending = isResubmissionSelected && hasSetResubmissionPermission && resubmission === undefined;
    const hasFutureResubmission = typeof resubmission === 'number' && resubmission > Date.now();
    const showResubmissionInput =
        isResubmissionSelected && hasSetResubmissionPermission && resubmission !== undefined && !hasFutureResubmission;
    const effectiveResubmissionDate = resubmissionDate ?? getDefaultResubmissionDate();
    const isResubmissionInPast = showResubmissionInput && effectiveResubmissionDate.getTime() < Date.now();
    const hasMailTrigger =
        !!targetStatus &&
        !!selectedSubStatus &&
        hasMailTriggerForSubstatus(statusesWithMailTrigger, targetStatus, selectedSubStatus);
    const showPreview = hasAnyMailTrigger(statusesWithMailTrigger, targetStatus);

    const preview = useStatusMailPreview({
        enabled: open && hasChangeStatusPermission && hasMailTrigger,
        leadId,
        request:
            targetStatus && selectedSubStatus
                ? ({ status: targetStatus, subStatus: selectedSubStatus } as SelectedStatusData)
                : null,
    });
    const isPreviewPending = hasMailTrigger && (preview.isFetching || preview.isPlaceholderData);
    const isPreviewForbidden = preview.error instanceof APIError && preview.error.status === 403;
    // non-403 errors keep the question; during a reload the previous answer stays (no jump), saving waits
    const showEmailNotification =
        hasMailTrigger &&
        (preview.data?.kind === STATUS_MAIL_PREVIEW_KINDS.PREVIEW || (preview.isError && !isPreviewForbidden));

    const prefilledNote = (() => {
        if (!selectedSubStatus || !targetStatus) return '';
        const translationPath = getSubStatusTranslationPath({
            status: targetStatus,
            subStatus: selectedSubStatus,
        } as SelectedStatusData);
        const subStatusLabel = translationPath ? t(translationPath) : selectedSubStatus;
        const parts = [t('STATUS.NOTE_PREFILL.STATUS_CHANGE', { substatus: subStatusLabel })];
        if (showEmailNotification && sendEmail) {
            parts.push(t('STATUS.NOTE_PREFILL.EMAIL_SENT'));
        }
        return parts.join(' ');
    })();

    const note = useStatusChangeNote({ open, prefill: prefilledNote });

    if (!targetStatus) return null;

    const subStatuses = loanRequestStatusesWithSubStatuses[targetStatus];

    const handleSubstatusSelect = (subStatus: SUB_STATUSES_TYPE) => {
        const nextSubStatus = selectedSubStatus === subStatus ? undefined : subStatus;
        setSelectedSubStatus(nextSubStatus);
        resetEmailChoice();
    };

    const handleSave = () => {
        if (!hasChangeStatusPermission) return;
        if (showEmailNotification && sendEmail === null) {
            emailChoice.markMissing();
            return;
        }

        const statusData = {
            sendEmail: showEmailNotification ? !!sendEmail : false,
            status: targetStatus,
            subStatus: selectedSubStatus,
        } as SelectedStatusData;
        const trimmedNote = hasCreateNotePermission ? note.noteToSave : '';
        const resubmissionTimestamp = showResubmissionInput ? effectiveResubmissionDate.getTime() : undefined;
        if (resubmissionTimestamp !== undefined && resubmissionTimestamp < Date.now()) {
            // re-render so the past-time error shows instead of a backend rejection
            setResubmissionDate(new Date(resubmissionTimestamp));
            return;
        }
        const statusUnchanged =
            targetStatus === currentStatus && (selectedSubStatus ?? undefined) === (currentSubStatus ?? undefined);

        if (statusUnchanged && !trimmedNote && resubmissionTimestamp === undefined) {
            onClose();
            return;
        }

        mutation.mutate(
            {
                currentStatus,
                currentSubStatus,
                leadId,
                noteContent: trimmedNote || undefined,
                resubmissionTimestamp,
                statusData,
            },
            { onSuccess: onClose },
        );
    };

    // sections are only carded in the two-column layout next to the mail preview
    const cardClassName = showPreview ? 'p-[18px] bg-white border rounded-lg border-grey-stroke' : undefined;

    const leftColumn = (
        <div
            className={cn(
                'flex flex-col min-w-0',
                // design: cards sit in a grey sidebar next to the preview
                showPreview ? 'h-full gap-3 overflow-y-auto px-2.5 py-3 rounded-md bg-surface-light' : 'gap-8',
            )}
        >
            <div className={cardClassName}>
                {showPreview && <p className="mb-3 text-sm font-bold text-midnight">{t('STATUS.TITLES.SUB_STATUS')}</p>}
                <div className="flex flex-col gap-2">
                    {Object.values(subStatuses).map((subStatus) => {
                        const statusData = { status: targetStatus, subStatus } as SelectedStatusData;
                        const translationPath = getSubStatusTranslationPath(statusData);
                        const isSelected = selectedSubStatus === subStatus;
                        const hasMailTemplate = hasMailTriggerForSubstatus(
                            statusesWithMailTrigger,
                            targetStatus,
                            subStatus,
                        );

                        return (
                            <button
                                key={subStatus}
                                type="button"
                                onClick={() => handleSubstatusSelect(subStatus)}
                                className={cn(
                                    'w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded text-sm text-left transition-colors border',
                                    isSelected
                                        ? 'bg-midnight text-white border-midnight'
                                        : 'bg-white text-midnight border-grey-stroke hover:bg-surface-light',
                                )}
                            >
                                <span className="truncate">{translationPath ? t(translationPath) : subStatus}</span>
                                <span className="flex items-center gap-2 shrink-0">
                                    {hasMailTemplate && (
                                        <LuMail className="size-3.5" data-testid="substatus-mail-template" />
                                    )}
                                    {isSelected && <LuCheck className="size-5" data-testid="active-substatus-check" />}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {showResubmissionInput && (
                <div className={cardClassName}>
                    <DateTimeInput
                        id="substatus-resubmission"
                        label={t('STATUS.RESUBMISSION.LABEL')}
                        layout={showPreview ? 'vertical' : 'horizontal'}
                        value={effectiveResubmissionDate}
                        onChange={setResubmissionDate}
                        minDate={new Date()}
                        minuteStep={RESUBMISSION_MINUTE_STEP}
                        error={isResubmissionInPast ? t('RESUBMISSION.TIME_PAST') : undefined}
                        className={showPreview ? undefined : 'gap-6'}
                        labelClassName={showPreview ? undefined : 'w-auto'}
                        inputClassName={showPreview ? undefined : 'w-[200px]'}
                    />
                </div>
            )}

            {isResubmissionSelected && hasFutureResubmission && (
                <div
                    className={cn(cardClassName, 'flex items-center gap-2.5 text-sm text-midnight')}
                    data-testid="substatus-resubmission"
                >
                    <LuAlarmClock className="size-[15px] shrink-0 text-blue-focus" aria-hidden />
                    <span>
                        {t('RESUBMISSION.DATE_TIME', {
                            date: dateFormat(resubmission),
                            time: timeFormat(resubmission),
                            weekday: weekdayFormat(resubmission),
                        })}
                    </span>
                </div>
            )}

            {hasCreateNotePermission && (
                <AddNoteCard
                    id="substatus-add-note"
                    className={showPreview ? undefined : 'p-0 border-0 rounded-none bg-transparent'}
                    content={note.content}
                    isEnabled={note.isEnabled}
                    onContentChange={note.onContentChange}
                    onToggle={note.onToggle}
                />
            )}
        </div>
    );

    const body = (
        <>
            {/* stable wrapper: the left column must not remount when the preview column appears */}
            <div
                className={cn(
                    showPreview && 'grid grid-cols-[290px_minmax(0,1fr)] gap-[18px]',
                    showPreview && STATUS_MAIL_DIALOG_HEIGHT.SUBSTATUS,
                )}
            >
                {leftColumn}
                {showPreview && (
                    <StatusMailPreview
                        emptyText={t('STATUS.SEND_MAIL.PREVIEW.CHOOSE_SUBSTATUS')}
                        isDimmed={showEmailNotification && sendEmail === false}
                        isError={hasMailTrigger && preview.isError}
                        isLoading={isPreviewPending && !preview.data}
                        // dim only a mail of the previous selection, not a background refetch of the current one
                        isRefreshing={hasMailTrigger && preview.isPlaceholderData}
                        preview={
                            hasMailTrigger || !selectedSubStatus
                                ? preview.data
                                : {
                                      kind: STATUS_MAIL_PREVIEW_KINDS.NO_TEMPLATE,
                                      reason: MAIL_FAILURE_REASONS.MISSING_DIVISION_TEMPLATE,
                                  }
                        }
                    />
                )}
            </div>

            <div className={cn('flex items-center justify-end gap-2', showPreview ? 'mt-4' : 'mt-8')}>
                {showPreview && (
                    <div className="mr-auto min-w-0">
                        <SendEmailChoice
                            id="send-status-email"
                            disabled={!showEmailNotification}
                            isMissing={emailChoice.isMissing}
                            value={sendEmail}
                            onChange={emailChoice.onChange}
                        />
                    </div>
                )}
                <Button
                    variant={ButtonVariant.SECONDARY}
                    iconLeft={<LuCircleX className="size-3.5" />}
                    onClick={onClose}
                >
                    {t('BUTTONS.CANCEL')}
                </Button>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    iconLeft={<LuSave className="size-[15px]" />}
                    onClick={handleSave}
                    // stays active without an email choice (marks it red instead); only waits for running requests
                    disabled={
                        mutation.isPending ||
                        isMailTriggersLoading ||
                        isPreviewPending ||
                        isResubmissionInPast ||
                        isResubmissionPending
                    }
                >
                    {t('STATUS.SAVE')}
                </Button>
            </div>
        </>
    );

    if (!withHeader) return body;

    return (
        <div className="flex flex-col px-6 pt-3 pb-6 bg-white rounded-lg">
            <div className={cn('flex items-center justify-between py-2', showPreview ? 'mb-3' : 'mb-4')}>
                <h3 className="text-lg font-semibold leading-7 tracking-[-0.44px] text-midnight">
                    {t('STATUS.SET_SUBSTATUS')}
                </h3>
                <button
                    type="button"
                    className="flex items-center justify-center size-6 text-midnight hover:text-midnight-hover transition-colors"
                    onClick={onClose}
                    aria-label={t('GENERAL_CLOSE_BUTTON')}
                >
                    <LuX className="size-5" />
                </button>
            </div>
            {body}
        </div>
    );
};
